import { Text } from "@chakra-ui/react";

import { cn } from "utils/helpers";
import { formatCurrency } from "./utils";

/**
 * Lightweight presentational preview of a SummaryTable used inside the
 * Create/Edit Summary Table modal. Mirrors the SummaryTable's visual
 * structure (header row of value-column names + Total, body row per quote
 * table, optional Column Totals footer) but renders sample `$0.00` cell
 * values rather than fetching live data — the summary table doesn't exist
 * server-side yet at preview time.
 *
 * Kept as a sibling of SummaryTable rather than refactoring it directly
 * because SummaryTable owns its own data-fetch / delete / edit affordances
 * that don't apply to an unsaved preview.
 */
export interface SummaryTablePreviewProps {
	categoryNames: string[];
	includeColumnTotals?: boolean;
	quoteTableIds: number[];
	quoteTableNames: Record<number, string>;
	summaryTableName: string;
}

const cellBorder = "border border-[#EAECF0]";
const placeholderText = "italic text-gray-400";

export const SummaryTablePreview: React.FC<SummaryTablePreviewProps> = ({
	categoryNames,
	includeColumnTotals = false,
	quoteTableIds,
	quoteTableNames,
	summaryTableName,
}) => {
	const hasRows = quoteTableIds.length > 0;
	const hasCols = categoryNames.length > 0;

	return (
		<div
			className="rounded-xl border border-[#EAECF0] bg-white shadow-sm"
			data-testid="summary-table-preview"
		>
			<div className="border-b border-[#EAECF0] px-4 py-3">
				<Text className="font-roboto text-sm font-semibold text-gray-900">
					{summaryTableName.trim() || "Untitled Summary Table"}
				</Text>
			</div>

			{!hasRows || !hasCols ? (
				<div className="px-4 py-8 text-center">
					<Text className="text-sm text-gray-500">
						{!hasRows
							? "Select one or more quote tables to preview rows."
							: "Select one or more value columns to preview columns."}
					</Text>
				</div>
			) : (
				<div className="overflow-x-auto">
					<table className="w-full table-auto">
						<thead>
							<tr className="bg-[#F9FAFB]">
								<th
									className={cn(
										cellBorder,
										"px-4 py-2 text-left font-roboto text-xs font-medium text-tertiary-600"
									)}
									style={{ minWidth: "180px" }}
								>
									Quote Table
								</th>
								{categoryNames.map((cat) => (
									<th
										key={cat}
										className={cn(
											cellBorder,
											"px-4 py-2 text-right font-roboto text-xs font-medium text-tertiary-600"
										)}
										style={{ minWidth: "120px" }}
									>
										{cat}
									</th>
								))}
								<th
									className={cn(
										cellBorder,
										"bg-gray-100 px-4 py-2 text-right font-roboto text-xs font-semibold text-gray-900"
									)}
									style={{ minWidth: "120px" }}
								>
									Total
								</th>
							</tr>
						</thead>
						<tbody>
							{quoteTableIds.map((qtId) => (
								<tr key={qtId} className="border-b border-[#EAECF0]">
									<td
										className={cn(
											cellBorder,
											"px-4 py-2 font-roboto text-sm text-gray-900"
										)}
									>
										{quoteTableNames[qtId] ?? `Table ${qtId}`}
									</td>
									{categoryNames.map((cat) => (
										<td
											key={`${qtId}-${cat}`}
											className={cn(
												cellBorder,
												"px-4 py-2 text-right font-roboto text-sm",
												placeholderText
											)}
										>
											{formatCurrency(0)}
										</td>
									))}
									<td
										className={cn(
											cellBorder,
											"bg-gray-50 px-4 py-2 text-right font-roboto text-sm font-medium",
											placeholderText
										)}
									>
										{formatCurrency(0)}
									</td>
								</tr>
							))}
							{includeColumnTotals && (
								<tr className="border-t-2 border-[#EAECF0] bg-gray-100">
									<td
										className={cn(
											cellBorder,
											"px-4 py-2 font-roboto text-sm font-semibold text-gray-700"
										)}
									>
										Column Totals
									</td>
									{categoryNames.map((cat) => (
										<td
											key={`col-total-${cat}`}
											className={cn(
												cellBorder,
												"px-4 py-2 text-right font-roboto text-sm font-semibold",
												placeholderText
											)}
										>
											{formatCurrency(0)}
										</td>
									))}
									<td
										className={cn(
											cellBorder,
											"bg-gray-100 px-4 py-2 text-right font-roboto text-sm font-bold",
											placeholderText
										)}
									>
										{formatCurrency(0)}
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};
